/** 库存管理相关接口 */
import ChildWmsSkuCombinedInfoDto from "@/dto/wms/ChildWmsSkuCombinedInfoDto";
import WmsProductDto from "@/dto/wms/WmsProductDto";
import WmsSkuDto from "@/dto/wms/WmsSkuDto";
import ShopType from "@/enum/ShopType";
import WmsSkuStatusType from "@/enum/wms/WmsSkuStatusType";
import WmsUpdateInventoryType from "@/enum/wms/WmsUpdateInventoryType";
import { WrapperList } from "@/utils/utilityTypes";
import mainServerClient from "../../http/mainServerClient";
import httpClient from "../../http/mainServerClient";
import TaskStatusEnum from "@/enum/TaskStatus";

export interface BriefProductInfo {
    originalId: number;

    simpleTitle: string;

    title: string;
}

interface GetBriefProductInfoParams {
    pageNo?: number;
    pageSize?: number;
    shopType?: ShopType | "";
    vagueTitleOrOriginalId?: string;
}

/** 查询商品简明信息 */
export function getBriefProductInfo(params: GetBriefProductInfoParams): Promise<WrapperList<BriefProductInfo>> {
    return httpClient.get("/wms/product/list:briefInfo", params);
}

export enum WmsSkuSort {
    SKU_EXTERNAL_ID_DESC = "SKU_EXTERNAL_ID_DESC",
    TOTAL_INVENTORY_ASC = "TOTAL_INVENTORY_ASC",
    TOTAL_INVENTORY_DESC = "TOTAL_INVENTORY_DESC",
}

export interface GetWmsProductListParams {
    /** 库存量起 */
    beginInventory?: number;
    /** 库存量止 */
    endInventory?: number;
    pageNo?: number;
    pageSize?: number;
    productOriginalId?: string;
    shopType?: ShopType | "";
    externalId?: string;
    /** 库存单品状态 */
    wmsSkuStatus?: WmsSkuStatusType;

    /** 组合商品弹窗使用 */
    vagueTitleOrOriginalId?: string;
    vagueRemark?: string;
    combinable?: boolean;
    wmsSkuSort: WmsSkuSort;
}

export function getWmsSkuList(params: GetWmsProductListParams): Promise<WrapperList<WmsSkuDto>> {
    return httpClient.get("/wms/wmsSku/list", params);
}

export interface SyncWmsProductResult {
    errorMsg?: string;
    shopId: number;
    status: "COMPLETED" | "ERROR" | "RUNNING";
}

/** 同步库存商品（重复调用以获取同步进度） */
export function syncWmsProduct(shopIds: number[]): Promise<SyncWmsProductResult[]> {
    return mainServerClient.post("/wms/wmsProduct/sync", { shopIds });
}

/** 根据平台商品 ID 同步库存商品（重复调用以获取同步进度） */
export function syncWmsProductByOriginalIds(shopId: number, originalIds: string[]): Promise<SyncWmsProductResult> {
    return mainServerClient.post("/wms/wmsProduct/syncByOriginalIds", { shopId, originalIds });
}

/** 批量更新库存单品库存警戒值 */
export function batchUpdateWmsSkuInventoryThreshold(inventoryThreshold: number, ids: number[]): Promise<void> {
    return mainServerClient.post("/wms/wmsSku/inventoryThreshold", { inventoryThreshold, ids });
}

interface BatchUpdateWmsSkusTotalInventory {
    ids: number[];

    inventory: number;

    remark: string;

    type: WmsUpdateInventoryType;
}

/** 批量更新指定库存单品实际库存 */
export function batchUpdateWmsSkusTotalInventory(params: BatchUpdateWmsSkusTotalInventory): Promise<void> {
    return mainServerClient.post("/wms/wmsSku/totalInventory", params);
}

/** 更新指定库存单品库存警戒值 */
export function updateWmsSkuInventoryThreshold(id: number, params: { inventoryThreshold: number }): Promise<WmsSkuDto> {
    return mainServerClient.post(`/wms/wmsSku/${id}/inventoryThreshold`, params);
}

/** 更新指定库存单品备注 */
export function updateWmsSkuRemark(id: number, params: { remark: string }): Promise<WmsSkuDto> {
    return mainServerClient.post(`/wms/wmsSku/${id}/remark`, params);
}

/** 更新指定库存单品库位号 */
export function updateWmsStockNo(id: number, params: { stockNo: string }): Promise<void> {
    return mainServerClient.post(`/wms/wmsSku/${id}/stockNo`, params);
}

/** 更新指定库存单品库位号 */
export function updateWmsTotalInventory(
    id: number,
    params: { inventory: number; remark?: string; type: WmsUpdateInventoryType },
): Promise<WmsSkuDto> {
    return mainServerClient.post(`/wms/wmsSku/${id}/totalInventory`, params);
}

export interface InventoryChangeRecord {
    dateCreated: string;
    id: number;
    operatorNickname: string;
    quantity: number;
    remark: string;
    salesInfo: string;
    skuExternalId: string;
    skuRemark: string;
    type: WmsUpdateInventoryType;
    wmsSkuId: number;
    /** 剩余库存 */
    remainingInventory: number;
}

export interface GetInventoryChangeRecordListParams {
    dateCreatedEnd?: string;
    dateCreatedStart?: string;
    pageNo: number;
    pageSize: number;
    type?: WmsUpdateInventoryType | "";
    skuExternalId?: string;
    vagueSkuRemark?: string;
    vagueRemark?: string;
}

export function getInventoryChangeRecordList(
    params: GetInventoryChangeRecordListParams,
): Promise<WrapperList<InventoryChangeRecord>> {
    return mainServerClient.get("/wms/inventoryChangeRecord/list", params);
}

export interface ScheduleDisableWmsTime {
    scheduleDisableWmsTime: string;
    shopId: number;
    shopName: string;
}

/** 获取店铺计划禁用库存管理功能记录列表 */
export function getListShopScheduleDisabledWmsRecords(): Promise<ScheduleDisableWmsTime[]> {
    return mainServerClient.get("/wms/listShopScheduleDisableWmsRecords");
}

/** 获取不扣除库存的订单标志列表 */
export function getNondeductibleInventoryOrderFlags(): Promise<string[]> {
    return mainServerClient.get("/wms/nondeductibleInventoryOrderFlags");
}

/** 更新不扣除库存的订单标志列表 */
export function updateNondeductibleInventoryOrderFlags(params: { orderFlags: string[] }): Promise<string[]> {
    return mainServerClient.post("/wms/nondeductibleInventoryOrderFlags", params);
}

export interface CreateWmsProductParams {
    skuExternalId: string;
    skuRemark?: string;
}

/** 新建库存商品 */
export function createWmsProduct(params: CreateWmsProductParams): Promise<WmsProductDto> {
    return mainServerClient.post(`/wms/wmsProduct`, params);
}

export interface CombineWmsProductParams {
    childWmsSkuInfos: {
        quantity: number;
        wmsSkuId: number;
    }[];
    parentWmsSkuId: number;
}

/** 创建组合库存商品 */
export function createCombineWmsProduct(params: CombineWmsProductParams): Promise<WmsSkuDto> {
    return mainServerClient.post(`/wms/wmsSku/combine`, params);
}

export function getChildWmsSkuCombinedInfos(wmsSkuId: number): Promise<ChildWmsSkuCombinedInfoDto[]> {
    return mainServerClient.get(`/wms/wmsSku/${wmsSkuId}/childWmsSkuCombinedInfos`, { id: wmsSkuId });
}

/** 组合商品变为普通商品 */
export function cancelCombineWmsProduct(parentWmsSkuId: number): Promise<void> {
    return mainServerClient.post("/wmsSku/cancel_combine", { parentWmsSkuId });
}

export interface InventoryWarningInfo {
    id: number; //库存Sku id
    inventoryThreshold: number; //预警阈值
    occupyInventory: number; //占用库存
    totalInventory: number; //总库存
    externalId: string; //SKU编码或商品编码
}

/** 获取库存预警的商品信息(返回的数据就是处于预警状态的数据)  */
export function getInventoryWarningList(): Promise<InventoryWarningInfo[]> {
    return mainServerClient.post("/wms/wmsSku/list_inventory_warning", {}, { hideModal: true });
}

interface WmsSkuOverview {
    /**
     * 是否有库存Sku设置了库存警戒值
     */
    anyInventoryThresholdConfigured: boolean;

    //库存预警WmsSku数量：剩余库存小于库存警戒值
    inventoryWarningCount: number;
}

export function getInventoryWarningOverview(): Promise<WmsSkuOverview> {
    return mainServerClient.get("/wms/wmsSku/overview", {}, { hideModal: true });
}

interface ExportWmsSkuParams {
    listParams?: Omit<GetWmsProductListParams, "pageSize" | "pageNo">;

    selectedWmsSkuIds?: number[];
    taskId?: string;
}

export interface WmsSkuDtoExportTaskResult {
    /**
     * 下载地址。
     */
    downloadUrl?: string;
    params?: {
        taskId: string;
    };
    /**
     * 导出任务状态。
     */
    status?: TaskStatusEnum;
}

export function exportWmsSku(params: ExportWmsSkuParams) {
    return mainServerClient.post<WmsSkuDtoExportTaskResult>("/wms/wmsSku/export", params);
}

export interface BatchDeleteWmsSkusResult {
    /**
     * 删除成功的 WmsSku 数量
     */
    deletedCount?: number;
    /**
     * 操作失败的 WmsSku 数量
     */
    failedCount?: number;
    /**
     * 失败原因映射（WmsSku.id -> 失败原因）
     */
    failedReasons?: Record<number, string>;
    /**
     * 隐藏成功的 WmsSku 数量
     */
    hiddenCount?: number;
}

/** 批量删除库存单品 */
export function deleteWmsSkus(wmsSkuIds: number[]) {
    return mainServerClient.post<BatchDeleteWmsSkusResult>("/wms/wmsSku/batch_delete", { ids: wmsSkuIds });
}

/** 批量恢复隐藏的库存单品。 */
export function restoreWmsSkus(wmsSkuIds: number[]) {
    return mainServerClient.post("/wms/wmsSku/batch_restore", { ids: wmsSkuIds });
}
